import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  Eye,
  ShoppingCart,
  Clock,
  CheckCircle,
  Truck,
  Package,
  XCircle,
  ArrowLeft
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersViewPage = ({ canUpdateStatus = false }) => {
  const { t } = useLanguage();
  const { isDistributor, user } = useAuth();
  const navigate = useNavigate();
  const isClient = user?.user_type === 'client';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success(t('savedSuccessfully'));
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: t('pending') },
      processing: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package, label: t('processing') },
      shipped: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Truck, label: t('shipped') },
      delivered: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: t('delivered') },
      cancelled: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: t('cancelled') },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <config.icon size={12} />
        {config.label}
      </Badge>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className={`space-y-6 animate-fade-in ${isClient ? 'max-w-2xl mx-auto' : ''}`} data-testid="orders-view-page">
      {/* Header */}
      <div>
        {isClient && (
          <button
            onClick={() => navigate('/client/supplements')}
            className="flex items-center gap-1.5 text-sm text-pink-500 font-semibold mb-3 hover:gap-2.5 transition-all duration-200"
            data-testid="back-to-store"
          >
            <ArrowLeft size={16} /> Volver a Tienda
          </button>
        )}
        <h1 className={`font-bold ${isClient ? 'text-3xl text-gray-900' : 'text-4xl text-[#467322]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {isClient ? 'Mi Carrito' : t('orders')}
        </h1>
        <p className={`mt-1 font-medium ${isClient ? 'text-sm text-gray-400' : 'text-gray-600'}`}>{isClient ? 'Mis pedidos realizados' : t('ordersList')}</p>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
              <Input
                placeholder={`${t('search')}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 border-[#467322]/30">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-[#467322]/20">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="processing">{t('processing')}</SelectItem>
                <SelectItem value="shipped">{t('shipped')}</SelectItem>
                <SelectItem value="delivered">{t('delivered')}</SelectItem>
                <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders */}
      {filteredOrders.length === 0 ? (
        <Card className="bg-white border border-[#467322]/20">
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noOrders')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Card key={order.id} className="bg-white border border-[#467322]/20 hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingCart size={18} className="text-[#467322]" />
                      <span className="font-mono text-sm text-gray-500">#{order.id.slice(0, 8)}</span>
                      {getStatusBadge(order.status)}
                    </div>
                    <p className="font-semibold text-black">{order.client_name}</p>
                    <p className="text-sm text-gray-500">
                      {order.items.length} productos • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{t('total')}</p>
                      <p className="text-xl font-bold text-[#467322]">${order.total.toFixed(2)}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDetailsModal(true);
                      }}
                      className="border-[#467322] text-[#467322] hover:bg-[#F2F7F0]"
                    >
                      <Eye size={16} className="mr-1" />
                      Ver
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('orderDetails')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#F2F7F0] rounded-lg">
                  <p className="text-sm text-gray-500">ID Pedido</p>
                  <p className="font-mono text-sm">{selectedOrder.id.slice(0, 8)}...</p>
                </div>
                <div className="p-3 bg-[#F2F7F0] rounded-lg">
                  <p className="text-sm text-gray-500">{t('status')}</p>
                  {getStatusBadge(selectedOrder.status)}
                </div>
              </div>

              {/* Status Update (if allowed) */}
              {(canUpdateStatus || isDistributor) && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-black">Actualizar Estado</p>
                  <Select 
                    value={selectedOrder.status} 
                    onValueChange={(value) => updateOrderStatus(selectedOrder.id, value)}
                  >
                    <SelectTrigger className="border-[#467322]/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-[#467322]/20">
                      <SelectItem value="pending">{t('pending')}</SelectItem>
                      <SelectItem value="processing">{t('processing')}</SelectItem>
                      <SelectItem value="shipped">{t('shipped')}</SelectItem>
                      <SelectItem value="delivered">{t('delivered')}</SelectItem>
                      <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Items */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-black">{t('items')}</p>
                <div className="border border-[#467322]/20 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#467322]">
                        <TableHead className="text-white">Producto</TableHead>
                        <TableHead className="text-white text-center">{t('quantity')}</TableHead>
                        <TableHead className="text-white text-right">{t('price')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items.map((item, idx) => (
                        <TableRow key={item.product_id || idx}>
                          <TableCell>{item.supplement_name}</TableCell>
                          <TableCell className="text-center">{item.quantity}</TableCell>
                          <TableCell className="text-right">${(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-[#467322] rounded-lg">
                <span className="text-white font-semibold">{t('total')}</span>
                <span className="text-2xl font-bold text-white">${selectedOrder.total.toFixed(2)}</span>
              </div>

              {/* Date */}
              <div className="p-3 bg-[#F2F7F0] rounded-lg">
                <p className="text-sm text-gray-500">{t('createdAt')}</p>
                <p className="font-semibold">{new Date(selectedOrder.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowDetailsModal(false)}
              className="bg-[#467322] hover:bg-[#3A601C] text-white"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersViewPage;
